import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  GitBranch,
  Plus,
  Search,
  Pencil,
  Trash2,
  Play,
  Pause,
  MessageSquare,
  Zap,
  Clock,
  Users,
  CheckCircle2,
  XCircle,
  Activity,
} from "lucide-react";

const EXEC_STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  completed: { label: "Concluída", color: "text-green-600", icon: CheckCircle2 },
  failed: { label: "Falhou", color: "text-red-600", icon: XCircle },
  running: { label: "Em execução", color: "text-blue-600", icon: Activity },
};

function FlowExecutionBadge({ flowId }: { flowId: number }) {
  const { data: executions = [] } = trpc.flows.listExecutions.useQuery(
    { flowId, limit: 5 },
    { refetchInterval: 10000 }
  );
  if (executions.length === 0) return null;
  const last = executions[0] as any;
  const cfg = EXEC_STATUS_CONFIG[last.status] ?? { label: last.status, color: "text-gray-500", icon: Activity };
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
      <span>
        Última execução: <span className={cfg.color}>{cfg.label}</span>
        {" · "}{executions.length} total
      </span>
    </div>
  );
}

const TRIGGER_LABELS: Record<string, string> = {
  keyword: "Palavra-chave",
  new_contact: "Novo contato",
  opt_in: "Opt-in",
  tag_added: "Tag adicionada",
  any_message: "Qualquer mensagem",
  broadcast: "Transmissão",
  // F0.3 — Gatilho webhook externo. Quando setado, o fluxo é disparado por
  // POST /api/webhooks/in/:urlToken — o token é gerado/configurado em
  // "Webhooks" (em Automações).
  webhook: "Webhook externo",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-gray-100 text-gray-600 border-gray-200",
  draft: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Ativo",
  inactive: "Inativo",
  draft: "Rascunho",
};

export default function WhatsAppFlows() {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    triggerType: "keyword",
    triggerValue: "",
  });

  const utils = trpc.useUtils();
  const { data: flows = [], isLoading } = trpc.flows.list.useQuery({ channel: 'whatsapp' });

  useContextoDeLista({
    titulo: "Fluxos do WhatsApp",
    itens: (flows ?? []).map((f: any) => ({ id: f.id, nome: f.name, status: f.status, gatilho: `${f.triggerType}${f.triggerValue ? `: ${f.triggerValue}` : ""}` })),
  });

  const [, navigate] = useLocation();

  // F0.3 UX-fix — quando o gatilho é "webhook", auto-criar o webhook ligado
  // ao fluxo recém-criado. Antes o usuário tinha que ir em Automações>Webhooks
  // manualmente e vincular o flowId — fluxo confuso. Agora a URL fica pronta
  // já no detalhe do fluxo via banner.
  const webhookCreateMut = trpc.webhooks.create.useMutation();

  const createMutation = trpc.flows.create.useMutation({
    onSuccess: async (created) => {
      utils.flows.list.invalidate();
      const wasWebhook = form.triggerType === "webhook";
      const flowName = form.name;
      setCreateOpen(false);
      setForm({ name: "", description: "", triggerType: "keyword", triggerValue: "" });

      if (created?.id && wasWebhook) {
        // Cria webhook vinculado imediatamente — usuário cai no builder com
        // URL pronta pra copiar (banner no topo).
        try {
          await webhookCreateMut.mutateAsync({
            name: `Webhook — ${flowName}`,
            flowId: created.id,
            isActive: true,
          });
          utils.webhooks.list.invalidate();
        } catch {
          // Não-fatal — usuário pode criar manualmente depois.
        }
      }

      if (created?.id) navigate(`/whatsapp-flows/${created.id}`);
    },
  });
  const updateMutation = trpc.flows.update.useMutation({
    onSuccess: () => utils.flows.list.invalidate(),
  });
  const deleteMutation = trpc.flows.delete.useMutation({
    onSuccess: () => { utils.flows.list.invalidate(); setDeleteId(null); },
  });

  const filtered = flows.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    (f.description ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleStatus = (flow: typeof flows[0]) => {
    const next = flow.status === "active" ? "inactive" : "active";
    updateMutation.mutate({ id: flow.id, status: next as any });
  };

  return (
    <PermissionGuard permission="whatsapp:manage_flows">
    <DashboardLayout>
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-green-600" />
            Fluxos de WhatsApp
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie fluxos automáticos de resposta para mensagens recebidas no WhatsApp
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Novo Fluxo
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total de Fluxos", value: flows.length, icon: GitBranch, color: "text-blue-500" },
          { label: "Ativos", value: flows.filter(f => f.status === "active").length, icon: Play, color: "text-green-500" },
          { label: "Rascunhos", value: flows.filter(f => f.status === "draft").length, icon: Pencil, color: "text-yellow-500" },
          { label: "Inativos", value: flows.filter(f => f.status === "inactive").length, icon: Pause, color: "text-gray-400" },
        ].map(stat => (
          <Card key={stat.label} className="border-border/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar fluxos..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Flow List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6 h-40 bg-muted/20 rounded-lg" />
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <MessageSquare className="w-12 h-12 text-muted-foreground/40" />
            <div className="text-center">
              <p className="font-medium text-muted-foreground">Nenhum fluxo encontrado</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                {search ? "Tente outro termo de busca" : "Crie seu primeiro fluxo de resposta automática"}
              </p>
            </div>
            {!search && (
              <Button onClick={() => setCreateOpen(true)} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Criar primeiro fluxo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(flow => (
            <Card key={flow.id} className="border-border/50 hover:border-primary/30 transition-colors group">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{flow.name}</CardTitle>
                    {flow.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{flow.description}</p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs shrink-0 ${STATUS_COLORS[flow.status ?? "draft"]}`}
                  >
                    {STATUS_LABELS[flow.status ?? "draft"]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <Zap className="w-3.5 h-3.5 text-yellow-500" />
                  <span>Gatilho: <strong>{TRIGGER_LABELS[flow.triggerType ?? "keyword"] ?? flow.triggerType}</strong></span>
                  {flow.triggerValue && (
                    <span className="bg-muted px-1.5 py-0.5 rounded font-mono">
                      "{flow.triggerValue}"
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Atualizado {new Date(flow.updatedAt!).toLocaleDateString("pt-BR")}</span>
                </div>
                <div className="mb-4">
                  <FlowExecutionBadge flowId={flow.id} />
                </div>
                <div className="flex items-center gap-2">
                  <Link href={`/whatsapp-flows/${flow.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full gap-1.5">
                      <Pencil className="w-3.5 h-3.5" />
                      Editar Fluxo
                    </Button>
                  </Link>
                  <Link href={`/whatsapp-flows/${flow.id}/executions`}>
                    <Button variant="outline" size="sm" title="Ver execuções">
                      <Users className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleStatus(flow)}
                    className={flow.status === "active" ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                  >
                    {flow.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(flow.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Novo Fluxo de WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Nome do Fluxo *</Label>
              <Input
                placeholder="Ex: Boas-vindas, Suporte, Vendas..."
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descreva o objetivo deste fluxo..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Gatilho</Label>
              <Select value={form.triggerType} onValueChange={v => setForm(f => ({ ...f, triggerType: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broadcast">Transmissão</SelectItem>
                  <SelectItem value="keyword">Palavra-chave</SelectItem>
                  <SelectItem value="any_message">Qualquer mensagem recebida</SelectItem>
                  <SelectItem value="new_contact">Novo contato</SelectItem>
                  {/* "Opt-in confirmado" saiu: nada no servidor dispara esse
                      evento, e o fluxo acabava respondendo a toda mensagem
                      recebida. Fluxos antigos que o usam continuam abrindo. */}
                  <SelectItem value="tag_added">Tag adicionada</SelectItem>
                  <SelectItem value="webhook">Webhook externo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.triggerType === "keyword" && (
              <div className="flex flex-col gap-1.5">
                <Label>Palavra-chave</Label>
                <Input
                  placeholder="Ex: OI, AJUDA, PREÇO..."
                  value={form.triggerValue}
                  onChange={e => setForm(f => ({ ...f, triggerValue: e.target.value.toUpperCase() }))}
                />
                <p className="text-xs text-muted-foreground">O fluxo será acionado quando o contato enviar esta palavra</p>
              </div>
            )}
            {form.triggerType === "webhook" && (
              <div className="rounded border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
                Este fluxo será disparado quando um sistema externo (Zapier,
                Make, formulário, API própria) enviar <code>POST</code> para
                <code> /api/webhooks/in/:urlToken</code>. Após criar o fluxo,
                configure o webhook em <strong>Automações &gt; Webhooks</strong> e
                aponte o <em>flowId</em> para este fluxo. Os campos do payload
                podem ser mapeados para os campos do contato.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate({ ...form, channel: 'whatsapp' })}
              disabled={!form.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Criando..." : "Criar e Editar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Fluxo</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O fluxo e todas as suas configurações serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
